import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  listenerProfile,
  profileSections,
  listenerStatuses,
  type ProfileSectionId,
} from '@/mocks/rfwListenerSocialProfile';

export default function PerfilOyenteSection() {
  const [activeTab, setActiveTab] = useState<'perfil' | 'secciones' | 'estatus'>('perfil');
  const profile = listenerProfile;

  const tabs = [
    { id: 'perfil' as const, label: 'Perfil', icon: 'ri-user-line' },
    { id: 'secciones' as const, label: 'Secciones', icon: 'ri-layout-grid-line' },
    { id: 'estatus' as const, label: 'Estatus', icon: 'ri-shield-star-line' },
  ];

  const sectionIcons: Record<ProfileSectionId, string> = {
    actividad: 'ri-flashlight-line',
    clubes: 'ri-group-line',
    logros: 'ri-medal-2-line',
    guardados: 'ri-bookmark-line',
    eventos: 'ri-calendar-event-line',
    concursos: 'ri-trophy-line',
    seguidores: 'ri-user-heart-line',
    siguiendo: 'ri-user-star-line',
  };

  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-pink-100 text-pink-700 text-xs font-semibold mb-4">
            <span className="w-2 h-2 rounded-full bg-pink-500 animate-pulse"></span>
            PERFIL SOCIAL DEL OYENTE
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            El Perfil Debe <span className="text-pink-600">Sentirse Importante</span>
          </h2>
          <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
            Un perfil social con identidad propia: foto, portada, nivel, insignias, ranking y 8 secciones que muestran todo lo que hacés en la comunidad. Porque cada oyente merece brillar.
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
          {[
            { label: 'Nivel', value: profile.level.name, sub: `N°${profile.level.number}`, icon: 'ri-bar-chart-line', color: 'text-pink-600 bg-pink-50' },
            { label: 'Ranking', value: `#${profile.stats.ranking}`, sub: 'Global RFW', icon: 'ri-trophy-line', color: 'text-amber-600 bg-amber-50' },
            { label: 'Puntos', value: profile.stats.totalPoints.toLocaleString(), sub: 'Score RFW', icon: 'ri-star-line', color: 'text-orange-600 bg-orange-50' },
            { label: 'Seguidores', value: profile.stats.followers.toLocaleString(), sub: profile.stats.following + ' seguidos', icon: 'ri-user-heart-line', color: 'text-rose-600 bg-rose-50' },
            { label: 'Racha', value: profile.stats.rachaDias + ' días', sub: 'Activo ' + profile.stats.diasActivo + ' días total', icon: 'ri-flashlight-line', color: 'text-emerald-600 bg-emerald-50' },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70">
              <div className={`w-9 h-9 rounded-full ${stat.color} flex items-center justify-center shrink-0`}>
                <i className={`${stat.icon} text-sm`}></i>
              </div>
              <div>
                <p className="text-lg font-bold text-foreground-950">{stat.value}</p>
                <p className="text-[10px] text-foreground-500">{stat.label}</p>
                {stat.sub && <p className="text-[9px] text-foreground-400">{stat.sub}</p>}
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-center mb-8">
          <div className="inline-flex bg-background-100 rounded-full p-1 border border-background-200/70">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'bg-pink-500 text-white shadow-sm'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                <i className={`${tab.icon} text-[10px]`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab: Perfil */}
        {activeTab === 'perfil' && (
          <div className="space-y-6">
            {/* Cover + Avatar Card */}
            <div className="rounded-2xl overflow-hidden border border-background-200/70 bg-background-50">
              <div className="h-32 md:h-44 relative overflow-hidden">
                <img src={profile.coverUrl} alt={`Portada de ${profile.name}`} className="w-full h-full object-cover object-top" />
                <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/50"></div>
              </div>
              <div className="px-4 md:px-6 pb-5 relative">
                <div className="flex flex-col sm:flex-row sm:items-end gap-4 -mt-12 sm:-mt-16 relative z-10">
                  <img
                    src={profile.photoUrl}
                    alt={profile.name}
                    className="w-24 h-24 md:w-32 md:h-32 rounded-full border-4 border-background-50 object-cover object-top ring-2 ring-pink-200 shrink-0"
                  />
                  <div className="flex-1 pt-2 sm:pt-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-lg md:text-xl font-bold text-foreground-950">{profile.name}</h3>
                      <span className="text-sm text-foreground-500">{profile.username}</span>
                    </div>
                    <p className="text-xs text-foreground-500 mt-0.5">
                      <i className="ri-map-pin-line text-[10px] mr-0.5"></i>
                      {profile.city}, {profile.country} {profile.countryFlag}
                      <span className="mx-1.5">·</span>
                      <i className="ri-calendar-line text-[10px] mr-0.5"></i>
                      Desde {new Date(profile.joinDate).toLocaleDateString('es-MX', { year: 'numeric', month: 'long' })}
                    </p>
                    <p className="text-sm text-foreground-700 mt-2 line-clamp-2">{profile.bio}</p>
                  </div>
                </div>

                {/* Level Bar */}
                <div className="mt-4 p-3 rounded-xl bg-pink-50 border border-pink-100/50">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-pink-500 flex items-center justify-center">
                        <i className="ri-bar-chart-line text-white text-xs"></i>
                      </div>
                      <span className="text-xs font-bold text-pink-700">{profile.level.name} · Nivel {profile.level.number}</span>
                    </div>
                    <span className="text-[10px] text-pink-600 font-medium">{profile.level.pointsToNext} pts para {profile.level.nextLevel}</span>
                  </div>
                  <div className="h-2 rounded-full bg-pink-200 overflow-hidden">
                    <div className="h-full rounded-full bg-pink-500 transition-all duration-500" style={{ width: `${profile.level.progress}%` }}></div>
                  </div>
                </div>

                {/* Badges */}
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  {profile.badges.map((badge) => (
                    <span key={badge.name} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold ${badge.bgColor} ${badge.color}`}>
                      <i className={`${badge.icon} text-[9px]`}></i>
                      {badge.name}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab: Secciones */}
        {activeTab === 'secciones' && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {profileSections.map((section) => (
                <Link
                  key={section.id}
                  to={section.link}
                  className={`p-4 rounded-2xl ${section.bgColor} border border-background-200/70 hover:shadow-sm transition-all duration-200 group cursor-pointer`}
                >
                  <div className={`w-9 h-9 rounded-full bg-white/70 flex items-center justify-center mb-2`}>
                    <i className={`${section.icon} ${section.color} text-base`}></i>
                  </div>
                  <p className="text-xs font-bold text-foreground-950">{section.name}</p>
                  <p className="text-[10px] text-foreground-500 mt-0.5">{section.description}</p>
                  <p className={`text-lg font-bold ${section.color} mt-1.5`}>{section.count.toLocaleString()}</p>
                </Link>
              ))}
            </div>

            {/* Section Summary Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="md:col-span-2 p-5 rounded-2xl bg-background-100 border border-background-200/70">
                <h4 className="text-sm font-bold text-foreground-950 mb-3">Las 8 Secciones de tu Perfil</h4>
                <div className="space-y-2">
                  {profileSections.map((section) => (
                    <div key={section.id} className="flex items-center gap-3 p-2.5 rounded-xl bg-background-50 border border-background-200/70">
                      <div className={`w-8 h-8 rounded-full ${section.bgColor} flex items-center justify-center shrink-0`}>
                        <i className={`${section.icon} ${section.color} text-sm`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-foreground-950">{section.name}</p>
                        <p className="text-[9px] text-foreground-500">{section.description}</p>
                      </div>
                      <span className="text-sm font-bold text-foreground-950">{section.count.toLocaleString()}</span>
                      <Link to={section.link} className="text-[10px] text-pink-600 font-medium hover:underline whitespace-nowrap">Ver</Link>
                    </div>
                  ))}
                </div>
              </div>
              <div className="p-5 rounded-2xl bg-pink-50 border border-pink-100/50">
                <h4 className="text-sm font-bold text-foreground-950 mb-3">Lo Que Mostrás</h4>
                <div className="space-y-2">
                  {['Top Fan', 'VIP', 'Embajador', 'Ganador', 'Campeón'].map((status) => (
                    <div key={status} className="flex items-center gap-2 p-2 rounded-lg bg-white/70">
                      <div className="w-2 h-2 rounded-full bg-pink-500"></div>
                      <span className="text-xs font-medium text-foreground-700">{status}</span>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-foreground-500 mt-3">Tus estatus aparecen destacados en todo tu perfil, comentarios y rankings.</p>
              </div>
            </div>
          </div>
        )}

        {/* Tab: Estatus */}
        {activeTab === 'estatus' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {listenerStatuses.map((status) => (
                <div key={status.id} className={`p-4 rounded-2xl ${status.bgColor} border border-background-200/70 text-center`}>
                  <div className={`w-12 h-12 rounded-full ${status.color} flex items-center justify-center mx-auto mb-3 ring-4 ${status.ringColor}`}>
                    <i className={`${status.icon} text-white text-xl`}></i>
                  </div>
                  <p className={`text-sm font-bold ${status.textColor} mb-1`}>{status.name}</p>
                  <p className="text-[9px] text-foreground-500 mb-2">{status.level}</p>
                  <p className="text-[10px] text-foreground-600 leading-relaxed line-clamp-3">{status.description}</p>
                  <p className="text-[9px] text-foreground-400 mt-2">Desde {new Date(status.earnedDate).toLocaleDateString('es-MX', { year: 'numeric', month: 'short' })}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="text-center mt-10">
          <Link
            to="/perfil-social-oyente"
            className="inline-flex items-center gap-2 px-6 py-3 bg-pink-500 hover:bg-pink-600 text-white font-semibold rounded-full transition-all duration-200 text-sm whitespace-nowrap"
          >
            <i className="ri-user-line"></i>
            Ver Perfil Completo
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {/* Regla Final */}
        <div className="mt-10 p-6 rounded-2xl bg-pink-100/70 border border-pink-200/50 text-center">
          <div className="w-10 h-10 rounded-full bg-pink-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-user-star-line text-lg text-white"></i>
          </div>
          <p className="text-sm font-bold text-foreground-950 mb-1">
            El perfil debe sentirse importante.
          </p>
          <p className="text-xs text-foreground-600 max-w-lg mx-auto">
            Porque cada oyente tiene una historia que contar. Un perfil que muestra foto, portada, nivel, insignias, estatus y toda tu actividad — desde los clubes donde participás hasta los concursos que ganaste. No es solo un perfil, es tu identidad en la comunidad radial más grande.
          </p>
        </div>
      </div>
    </section>
  );
}